'use client'

import { useState } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Calendar, Trash2, DollarSign } from "lucide-react"
import { Button } from "@/components/ui/button"
import { EditProductServiceForm } from "./EditProductServiceForm"
import FormModal from "@/components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Edit2, Loader2 } from "lucide-react"
import ProductService from '@/api/productservices.service'
import { useToast } from '@/hooks/use-toast'
 
// This type is used to define the shape of our data.
export type ProductService = {
  id: number;
  publicId: string;
  name: string;
  description: string;
  unitPrice: number;
  currency: string;
  dateUpdated: string;
  dateCreated: string;
}

export const columns: ColumnDef<ProductService>[] = [
  {
    header: "Actions",
    cell: ({ row }) => {
      const [isModalOpen, setModalOpen] = useState(false);
      const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
      const [isRemoving, setIsRemoving] = useState(false);
      const { toast } = useToast();

      const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
        toast({
          title: title,
          description: description,
          variant: toastType === "error" ? "destructive" : "default",
          duration: duration,
        });
      };

      const handleRemove = async () => {
        setIsRemoving(true);
        try {
          const response = await ProductService.deleteProductService(row.original.id);
          if (response.data.result === "success") {
            showToastWithTimeout("success", "✅ Product/Service Deleted", response.data.message, 3000);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            showToastWithTimeout("error", "Error", response.data.message, 3000);
          }
        } catch (error) {
          console.log(error);
          showToastWithTimeout("error", "Error", "Failed to remove product/service.", 3000);
        } finally {
          setIsRemoving(false);
        }
      };

      return (
        <>
          <FormModal
            open={isModalOpen}
            setOpen={setModalOpen}
            title="Update Product/Service"
          >
            <EditProductServiceForm productService={row.original} />
          </FormModal>
          <FormModal
            open={isDeleteModalOpen}
            setOpen={setDeleteModalOpen}
            title="Delete Product/Service"
          >
            <div className="space-y-4">
              <p>Are you sure you want to delete <b>{row.original.name}</b>? This action cannot be undone.</p>
              <Button onClick={handleRemove} className="bg-red-600 text-white">
                {isRemoving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Confirm Removal"}
              </Button>
            </div>
          </FormModal>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreHorizontal className="w-5 h-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setModalOpen(true)}>
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDeleteModalOpen(true)}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      );
    },
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Name
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="flex items-center whitespace-nowrap">
        <span className="font-medium text-gray-900">{row.original.name}</span>
      </div>
    ),
  },
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => (
      <span className="text-gray-700">{row.original.description}</span>
    ),
  },
  {
    accessorKey: "unitPrice",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        <DollarSign className="w-4 h-4 mr-2" />
        Unit Price
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => (
      <span className="text-gray-700">{row.original.unitPrice} {row.original.currency}</span>
    ),
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        <Calendar className="w-4 h-4 mr-2" />
        Date Created
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => (
      <span className="text-gray-500">{row.original.dateCreated}</span>
    ),
  },
]
